import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Button, Dropdown, Loading } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findLayoutSettings, updateLayoutSettings } from '../actions';

const layoutOptions = [
  { value: 'RETAIL_MODE', label: 'Retail Mode' },
  { value: 'SERVICE_MODE', label: 'Service Mode' },
  { value: 'RETAIL_ADVANCED_MODE', label: 'Retail Advanced Mode' },
];

const LayoutSettings = memo(
  ({
    layoutSettingsState: { layoutSettingsData },
    showAlert,
    findLayoutSettings,
    updateLayoutSettings,
  }) => {
    const originalDataRef = useRef({});
    const [layoutSettingsForm, setLayoutSettingsForm] = useState({
      'cashier-layout': '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      findLayoutSettings();
    }, []);

    useEffect(() => {
      if (
        Array.isArray(layoutSettingsData) &&
        layoutSettingsData.length > 0
      ) {
        const nextForm = {
          'cashier-layout': '',
        };
        for (const item of layoutSettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setLayoutSettingsForm(nextForm);
      }
    }, [layoutSettingsData]);

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(layoutSettingsForm).forEach((key) => {
        const currentVal = layoutSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const layoutSettings = {
        layoutSettingsData: changedFields,
      };
      const result = await updateLayoutSettings(layoutSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Layout settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update layout settings!',
        });
      }
      setIsLoading(false);
      findLayoutSettings();
    };

    const onClickCancel = () => {
      navigate('/');
    };

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Layout Settings</h1>
            <p className={styles.breadcrumb}>Settings / Layout</p>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Cashier Layout Settings</h2>
                  <p className={styles.topicCaption}>
                    Here you can change the cashier layout settings
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="cashier-layout"
                    label="Cashier Layout"
                    value={layoutSettingsForm['cashier-layout']}
                    onChange={(value) =>
                      setLayoutSettingsForm((prev) => ({ ...prev, 'cashier-layout': value }))
                    }
                    options={layoutOptions}
                    placeholder="Select layout"
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  layoutSettingsState: state.layoutSettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  updateLayoutSettings,
  findLayoutSettings,
})(LayoutSettings);
